import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, Megaphone, X } from 'lucide-react';

export type ToastKind = 'info' | 'success' | 'error' | 'announcement';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  body?: string;
  /** ms before auto-dismiss; 0 keeps it until dismissed. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((previous) => previous.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((previous) => [...previous.slice(-4), { ...toast, id }]);
      const duration = toast.duration ?? (toast.kind === 'error' ? 8000 : 5000);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

const ICONS: Record<ToastKind, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  error: AlertTriangle,
  announcement: Megaphone,
};

const TONES: Record<ToastKind, string> = {
  info: 'ring-1 ring-inset ring-white/70',
  success: 'ring-1 ring-inset ring-emerald-400/50',
  error: 'ring-1 ring-inset ring-rose-400/50',
  announcement: 'ring-1 ring-inset ring-brand-400/60',
};

const ICON_TONES: Record<ToastKind, string> = {
  info: 'text-slate-500',
  success: 'text-emerald-600',
  error: 'text-rose-600',
  announcement: 'text-brand-600',
};

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
      // Announcements must reach people who are not looking at this region.
      role="region"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => {
        const Icon = ICONS[toast.kind];
        return (
          <div
            key={toast.id}
            className={`glass-strong pointer-events-auto w-full max-w-sm animate-slide-up rounded-3xl p-4 ${TONES[toast.kind]}`}
          >
            <div className="flex gap-3">
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${ICON_TONES[toast.kind]}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-900">{toast.title}</p>
                {toast.body ? (
                  <p className="mt-0.5 whitespace-pre-line text-sm text-slate-600">{toast.body}</p>
                ) : null}
                {toast.action ? (
                  <button
                    type="button"
                    className="mt-2 text-sm font-semibold text-brand-600 hover:underline"
                    onClick={() => {
                      toast.action?.onClick();
                      onDismiss(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                aria-label="Dismiss notification"
                className="text-slate-400 transition hover:text-slate-600"
                onClick={() => onDismiss(toast.id)}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
